import type { ApiContext } from "@hobbyshopify/backend-client";
import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  type LocalAudioTrack,
  type LocalVideoTrack,
  type RemoteParticipant,
  type RemoteTrack,
  Room,
  RoomEvent,
  Track,
  TrackEvent,
  VideoPresets,
  type VideoResolution,
} from "livekit-client";
import { ChangeNotifier } from "subtree.js/core";
import type { AuthContextProvider } from "trunk.js";
import type { BackendApi } from "../ops/api";
import { mergeWire, preferWideCamera, summarizeVideoStats, type Wire, type WireSample } from "../primitives/wire-stats";
import { PortraitProcessor } from "./portrait-processor";

/**
 * Portrait, 1080x1920.
 *
 * A break is hosted on a phone held upright and watched on one, so vertical is
 * the shape of the product rather than a preference: a landscape capture is
 * letterboxed into a thin strip on the buyer's screen, and the card being
 * pulled, the thing being sold, ends up a few hundred pixels tall.
 *
 * All of it is ideal, not exact, and a laptop webcam ignores it: it answers
 * with 16:9 or a square. `PortraitProcessor` is what actually guarantees the
 * shape, by cutting the middle 9:16 out of whatever arrives.
 */
const PORTRAIT_1080: VideoResolution = {
  width: VideoPresets.h1080.height,
  height: VideoPresets.h1080.width,
  frameRate: 30,
  aspectRatio: 9 / 16,
};

export type BroadcastPhase = "ready" | "connecting" | "live" | "error" | "unsupported";

/** A buyer the host brought on, voice only, as the room reports them. */
export type StudioGuest = { identity: string; muted: boolean; speaking: boolean };

export type ActiveBroadcast = {
  streamId: string;
  title: string;
  /** The stream is already LIVE and this is its host: come back without a press. */
  resumeAsHost: boolean;
};

/** Same broadcast, same object, so nothing re-renders for nothing. */
function samePresence(current: ActiveBroadcast | null, next: ActiveBroadcast): boolean {
  return (
    current?.streamId === next.streamId && current.title === next.title && current.resumeAsHost === next.resumeAsHost
  );
}

/**
 * The host's camera, published from the browser with nothing installed.
 *
 * A service that lives for the app rather than for a page: it used to live in
 * the cockpit, so the router unmounted it the moment the host opened anything
 * else, and unmounting stops the local track and disconnects the room. Here the
 * camera survives every in-app navigation. What it cannot survive is the host
 * switching apps or locking the phone: the browser suspends the page and the OS
 * takes the camera back. That is a platform limit, which is why this holds a
 * wake lock and the shell says plainly that the broadcast is still running.
 *
 * `present` is how a cockpit says which show the camera belongs to; leaving the
 * page is not ending the stream, so it never dismisses on its own. `dismiss` is
 * the stream being over.
 *
 * Nothing here flips the stream to LIVE. That is the provider reporting a video
 * track published, over its webhook or `sync-stream`. A local button claiming
 * "live" lies the moment ingest silently fails, and the lie reaches everyone watching.
 */
export class BroadcastStudio extends ChangeNotifier {
  readonly #client: Pick<BackendApi, "live">;
  readonly #session: AuthContextProvider<ApiContext>;

  #active: ActiveBroadcast | null = null;
  #phase: BroadcastPhase = "ready";
  #error: string | null = null;
  #cameras: readonly MediaDeviceInfo[] = [];
  #cameraId = "";
  #wire: Wire | null = null;
  #interrupted = false;
  #reconnecting = false;

  #video: HTMLVideoElement | null = null;
  #room: Room | null = null;
  #track: LocalVideoTrack | null = null;
  #mic: LocalAudioTrack | null = null;
  #micMuted = false;
  #poll: ReturnType<typeof setInterval> | null = null;
  #watchdog: ReturnType<typeof setInterval> | null = null;
  #wakeLock: { release?: () => Promise<void> } | null = null;
  /** One attempt per presented stream: a failed auto-resume falls to the manual button rather than fighting the host in a loop. */
  #autoResumeTried = false;
  /**
   * The host pressed Stop and meant it. Auto-resume keys on phase `ready`,
   * which is exactly what `stop` sets, so without this a host who stopped the
   * camera on a live stream would be put straight back on air.
   */
  #stoppedByHand = false;
  /** `start` is not re-entrant: a double tap built a second Room and lost the first. */
  #starting = false;
  #liveListenersOn = false;
  #guests: readonly StudioGuest[] = [];
  /** The guests' voices: one element per track, since one element plays one audio track. */
  #guestAudio = new Set<HTMLMediaElement>();

  constructor(o: { client: Pick<BackendApi, "live">; session: AuthContextProvider<ApiContext> }) {
    super();
    this.#client = o.client;
    this.#session = o.session;
  }

  get active(): ActiveBroadcast | null {
    return this.#active;
  }

  /** The camera on this device is publishing right now. */
  get onAir(): boolean {
    return this.#phase === "live";
  }

  get phase(): BroadcastPhase {
    return this.#phase;
  }

  get error(): string | null {
    return this.#error;
  }

  get cameras(): readonly MediaDeviceInfo[] {
    return this.#cameras;
  }

  get cameraId(): string {
    return this.#cameraId;
  }

  get wire(): Wire | null {
    return this.#wire;
  }

  /** The camera died while the room stayed up: viewers see a frozen picture. */
  get interrupted(): boolean {
    return this.#interrupted;
  }

  get reconnecting(): boolean {
    return this.#reconnecting;
  }

  /** False when this device publishes no microphone at all. */
  get hasMic(): boolean {
    return this.#mic !== null;
  }

  get micMuted(): boolean {
    return this.#micMuted;
  }

  /** Guests on the stream, so the host hears them and sees who is muted or talking. */
  get guests(): readonly StudioGuest[] {
    return this.#guests;
  }

  /** Said by the cockpit: this is the show the camera belongs to. */
  present(broadcast: ActiveBroadcast): void {
    if (samePresence(this.#active, broadcast)) return;
    // One camera, one stream. Standing on a different stream's cockpit means the
    // first broadcast is over; stopping it here is what keeps the device from
    // publishing into a room nobody is looking at.
    if (this.#active && this.#active.streamId !== broadcast.streamId) {
      this.stop();
      this.#stoppedByHand = false;
      this.#autoResumeTried = false;
    }
    this.#active = broadcast;
    this.notifyListeners();
    this.#maybeAutoResume();
  }

  /** The stream is over. Takes the stream id and ignores a mismatch, so a page unmounting late cannot take down another stream. */
  dismiss(streamId: string): void {
    if (this.#active?.streamId !== streamId) return;
    this.stop();
    this.#active = null;
    this.notifyListeners();
  }

  /**
   * Where the picture goes. The stage mounts one `<video>` for as long as a
   * broadcast is active and only hides it off-route: re-attaching a published
   * track to a new element is a preview that can fail to come back.
   */
  attachVideo(element: HTMLVideoElement | null): void {
    this.#video = element;
    if (element && this.#track) this.#track.attach(element);
  }

  start = (): void => {
    void this.#start();
  };

  stop = (): void => {
    this.#stoppedByHand = true;
    this.#teardown();
    this.#wire = null;
    this.#interrupted = false;
    this.#reconnecting = false;
    this.#setPhase("ready");
  };

  /**
   * Bring a dead camera back without reloading the page. A reload is the one
   * action that drops the LiveKit room and takes the stream down with it, so it
   * must never be the only fix.
   */
  restartCamera = (): void => {
    void this.#restartCamera();
  };

  /** Switching cameras mid-break is the difference between showing the box and showing the ceiling. */
  switchCamera = (deviceId: string): void => {
    this.#cameraId = deviceId;
    this.notifyListeners();
    this.#room
      ?.switchActiveDevice("videoinput", deviceId)
      .catch((caught) => this.#setError(caught instanceof Error ? caught.message : String(caught)));
  };

  toggleMic = (): void => {
    const mic = this.#mic;
    if (!mic) return;
    this.#micMuted = !this.#micMuted;
    this.notifyListeners();
    void (this.#micMuted ? mic.mute() : mic.unmute()).catch((caught) =>
      this.#setError(caught instanceof Error ? caught.message : String(caught)),
    );
  };

  override dispose(): void {
    this.#teardown();
    this.#setLiveListeners(false);
    super.dispose();
  }

  async #start(): Promise<void> {
    const streamId = this.#active?.streamId;
    if (!streamId || this.#starting) return;
    this.#starting = true;
    // Pressing Go live is the opposite of pressing Stop.
    this.#stoppedByHand = false;

    // Whatever was here before this attempt is finished with. Normally nothing;
    // after a failed start or a dropped room, a corpse.
    this.#teardown();
    this.#error = null;
    this.#setPhase("connecting");

    try {
      const provisioned = await this.#client.live.provisionStream(await this.#session.authorizeCall(), streamId);
      if (!provisioned.ok) {
        this.#error = provisioned.errors[0].message;
        this.#setPhase("error");
        return;
      }
      const credentials = provisioned.value;

      if (!credentials.browserBroadcast) {
        this.#error =
          "This stream's video provider cannot broadcast from a browser. Use OBS with the encoder details on Control.";
        this.#setPhase("unsupported");
        return;
      }

      // adaptiveStream and dynacast are OFF for the publisher: both exist to
      // reduce what gets sent, and the host's own feed must not be degraded.
      const room = new Room({ adaptiveStream: false, dynacast: false });
      this.#room = room;

      room.on(RoomEvent.Reconnecting, () => {
        this.#reconnecting = true;
        this.notifyListeners();
      });
      room.on(RoomEvent.Reconnected, () => {
        this.#reconnecting = false;
        this.notifyListeners();
      });

      // The room went away, which is a clean stop and not a dead camera. The
      // identity check matters: `start` tears down whatever came before it and
      // `disconnect` is async, so a previous room can fire this while the new
      // one is still connecting.
      room.on(RoomEvent.Disconnected, () => {
        if (this.#room !== room) return;
        this.#teardown();
        this.#wire = null;
        this.#interrupted = false;
        this.#reconnecting = false;
        this.#setPhase("ready");
      });

      room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => this.#hearGuest(track, participant));
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        for (const element of track.detach()) {
          element.remove();
          this.#guestAudio.delete(element);
        }
        this.#readGuests(room);
      });
      for (const event of [
        RoomEvent.TrackMuted,
        RoomEvent.TrackUnmuted,
        RoomEvent.TrackPublished,
        RoomEvent.TrackUnpublished,
        RoomEvent.ActiveSpeakersChanged,
        RoomEvent.ParticipantDisconnected,
      ] as const) {
        room.on(event, () => this.#readGuests(room));
      }

      await room.connect(credentials.serverUrl, credentials.token);

      // Rear camera: a host is pointing the phone at cards, not at themselves.
      const videoTrack = await createLocalVideoTrack({ resolution: PORTRAIT_1080, facingMode: "environment" });
      this.#track = videoTrack;
      // Only where the camera ignored the portrait request (a laptop webcam). A
      // phone already shooting upright is published as it is, sparing its CPU.
      const { width = 0, height = 0 } = videoTrack.mediaStreamTrack.getSettings();
      if (width * 16 > height * 9) {
        try {
          await videoTrack.setProcessor(new PortraitProcessor());
        } catch {
          // No canvas capture here: the camera goes out in whatever shape it gives.
        }
      }
      if (this.#video) videoTrack.attach(this.#video);

      // The camera being suspended shows up here: backgrounding the browser or
      // locking the phone mutes or ends the capture track while LiveKit keeps
      // the connection open and keeps sending nothing.
      videoTrack.on(TrackEvent.Muted, () => this.#setInterrupted(true));
      videoTrack.on(TrackEvent.Unmuted, () => this.#setInterrupted(false));
      videoTrack.on(TrackEvent.Ended, () => this.#setInterrupted(true));

      await room.localParticipant.publishTrack(videoTrack, {
        videoEncoding: VideoPresets.h1080.encoding,
        // Simulcast on: viewers on bad connections get a lower layer instead of a stall.
        simulcast: true,
      });

      try {
        const audioTrack = await createLocalAudioTrack();
        await room.localParticipant.publishTrack(audioTrack);
        this.#mic = audioTrack;
        this.#micMuted = false;
      } catch {
        // A silent break is bad; a break that will not start is worse.
      }

      // Pick the wide lens if this device has one. `facingMode: "environment"`
      // gets *a* rear camera, and phones hand back the standard lens, which
      // crops tight enough that half of a card held over a table leaves frame.
      // After the first track exists, because labels are empty until camera
      // permission has been granted once.
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === "videoinput");
      this.#cameras = videoInputs;

      const current = videoTrack.mediaStreamTrack.getSettings().deviceId ?? "";
      const wide = preferWideCamera(videoInputs, current);
      this.#cameraId = current;
      if (wide) {
        try {
          await room.switchActiveDevice("videoinput", wide);
          this.#cameraId = wide;
        } catch {
          // Not fatal: the standard lens is already live, and the picker lets the host switch by hand.
        }
      }

      this.#interrupted = false;
      this.#setPhase("live");
      await this.#acquireWakeLock();

      // What is actually on the wire, as opposed to what was asked for.
      let previous: WireSample | null = null;
      this.#poll = setInterval(async () => {
        try {
          const stats = await this.#track?.getRTCStatsReport?.();
          if (!stats) return;
          const reading = summarizeVideoStats(
            stats.values() as unknown as Iterable<Record<string, unknown>>,
            previous,
            performance.now(),
          );
          if (!reading) return;
          previous = reading.sample;
          this.#wire = mergeWire(this.#wire, reading.wire);
          this.notifyListeners();
        } catch {
          // One missed sample. The next one is two seconds away.
        }
      }, 2_000);
    } catch (caught) {
      const detail = caught instanceof Error ? caught.message : String(caught);
      this.#error = /permission|denied|NotAllowed/i.test(detail)
        ? "Camera or microphone permission was denied. Allow access and try again."
        : detail;
      this.#teardown();
      this.#setPhase("error");
    } finally {
      this.#starting = false;
    }
  }

  async #restartCamera(): Promise<void> {
    const track = this.#track;
    if (!track) return;
    try {
      await track.restartTrack();
      if (this.#video) track.attach(this.#video);
      this.#interrupted = false;
      this.#error = null;
    } catch {
      this.#interrupted = true;
    }
    this.notifyListeners();
  }

  /**
   * The camera brings itself back on a live stream. A reload, a crash, or a phone
   * that died all land here the same way: the page mounts fresh in `ready` while
   * the stream is still LIVE and buyers are still in the room reading "back shortly".
   */
  #maybeAutoResume(): void {
    if (this.#stoppedByHand || this.#autoResumeTried) return;
    if (!this.#active?.resumeAsHost || this.#phase !== "ready") return;
    this.#autoResumeTried = true;
    void this.#start();
  }

  /**
   * Keep the screen awake while broadcasting. The most common way a break dies
   * is the phone locking itself after a minute of nobody touching it. Best
   * effort: older devices lack the API, and that must not block going live.
   */
  async #acquireWakeLock(): Promise<void> {
    try {
      const nav = navigator as Navigator & {
        wakeLock?: { request: (type: "screen") => Promise<{ release?: () => Promise<void> }> };
      };
      if (nav.wakeLock?.request) this.#wakeLock = await nav.wakeLock.request("screen");
    } catch {
      // No wake lock here. The stream still goes on.
    }
  }

  #hearGuest(track: RemoteTrack, participant: RemoteParticipant): void {
    if (track.kind !== Track.Kind.Audio || !participant.identity.startsWith("viewer-")) return;
    if (typeof document === "undefined" || !this.#room) return;
    const element = track.attach();
    element.hidden = true;
    document.body.appendChild(element);
    this.#guestAudio.add(element);
    this.#readGuests(this.#room);
  }

  #readGuests(room: Room): void {
    if (this.#room !== room) return;
    const guests: StudioGuest[] = [];
    for (const participant of room.remoteParticipants.values()) {
      if (!participant.identity.startsWith("viewer-")) continue;
      const mic = participant.getTrackPublication(Track.Source.Microphone);
      if (!mic) continue;
      guests.push({ identity: participant.identity, muted: mic.isMuted, speaking: participant.isSpeaking });
    }
    this.#guests = guests;
    this.notifyListeners();
  }

  #teardown(): void {
    if (this.#poll) clearInterval(this.#poll);
    this.#poll = null;
    try {
      this.#track?.stop();
      void this.#room?.disconnect();
    } catch {
      // Already gone.
    }
    this.#track = null;
    this.#mic = null;
    this.#micMuted = false;
    this.#room = null;
    for (const element of this.#guestAudio) element.remove();
    this.#guestAudio.clear();
    this.#guests = [];
    void this.#wakeLock?.release?.();
    this.#wakeLock = null;
  }

  #setPhase(phase: BroadcastPhase): void {
    this.#phase = phase;
    this.#setLiveListeners(phase === "live");
    this.notifyListeners();
  }

  #setError(message: string): void {
    this.#error = message;
    this.notifyListeners();
  }

  #setInterrupted(value: boolean): void {
    if (this.#interrupted === value) return;
    this.#interrupted = value;
    this.notifyListeners();
  }

  /** The page-level guards that only make sense while on air. */
  #setLiveListeners(on: boolean): void {
    if (on === this.#liveListenersOn || typeof document === "undefined") return;
    this.#liveListenersOn = on;
    if (on) {
      document.addEventListener("visibilitychange", this.#onVisibilityChange);
      window.addEventListener("beforeunload", this.#onBeforeUnload);
      // Notice a dead camera even while the page is in front: LiveKit's events
      // say nothing about a track that has quietly stopped producing frames.
      this.#watchdog = setInterval(() => {
        const media = this.#track?.mediaStreamTrack;
        if (!media) return;
        if (media.readyState === "ended" || media.muted) this.#setInterrupted(true);
      }, 3_000);
    } else {
      document.removeEventListener("visibilitychange", this.#onVisibilityChange);
      window.removeEventListener("beforeunload", this.#onBeforeUnload);
      if (this.#watchdog) clearInterval(this.#watchdog);
      this.#watchdog = null;
    }
  }

  /**
   * Recover when the host comes back to the page: the browser drops the wake
   * lock whenever the page hides, and a camera track the OS suspended stays
   * dead until it is explicitly restarted.
   */
  readonly #onVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState !== "visible") return;
    await this.#acquireWakeLock();
    const track = this.#track;
    const dead = track?.isMuted === true || track?.mediaStreamTrack?.readyState === "ended";
    if (dead) await this.#restartCamera();
  };

  /** Leaving the page while live gets the browser's own "are you sure": the reload and the closed tab take the stream with them. */
  readonly #onBeforeUnload = (event: BeforeUnloadEvent): void => {
    event.preventDefault();
    // Required by older Chrome for the dialog to appear; the text is never shown by modern browsers.
    event.returnValue = "";
  };
}
